import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRobot,
  faBolt,
  faEnvelopeOpenText,
  faLaptopCode,
  faPlug,
  faChartLine,
  faHeadset,
  faChartPie,
  faComments,
  faPenRuler,
  faCode,
  faRocket,
} from '@fortawesome/free-solid-svg-icons';
import Seo from './Seo';
import '../styles/About.css';

const About: React.FC = () => {
  return (
    <main>
      <Seo
        title="About HICO — AI Automation, Web Apps & Smart Integrations"
        description="HICO builds AI solutions, custom web apps, API and Zapier integrations, email systems, and high-performing websites — engineered to convert and built to scale."
        path="/about"
      />

      <section className="aboutHero">
        <span className="aboutEyebrow">What we do</span>
        <h1 className="pageTitle">
          We build the tools that run your business.
        </h1>
        <p className="heroSub aboutHeroSub">
          Websites, web apps, and automations that connect your tools and handle
          the repetitive work &mdash; so you and your team can focus on the parts
          that actually matter.
        </p>
        <div className="aboutHeroActions">
          <Link className="btn-primary heroBtn" to="/contact">
            Start a project
          </Link>
          <Link className="cta aboutHeroLink" to="/work">
            See the work &rarr;
          </Link>
        </div>
      </section>

      <section className="aboutBlock" aria-label="What we build">
        <div className="aboutBlockHead">
          <span className="aboutEyebrow">Capabilities</span>
          <h2 className="aboutBlockTitle">Smart solutions, built to convert.</h2>
          <p className="aboutBlockLede">
            Whatever you&rsquo;re building, we make the pieces work together &mdash;
            cleanly, quickly, and in a way your customers actually feel.
          </p>
        </div>

        <div className="valueProps aboutServicesGrid">
          <article className="valueCard">
            <FontAwesomeIcon icon={faRobot} className="valueIcon" />
            <h3 className="valueTitle">AI Solutions</h3>
            <p>
              Custom AI that drafts, answers, sorts, and decides &mdash; wired into
              your real workflows so it saves time from day one.
            </p>
            <Link className="cta" to="/contact">
              Automate with AI &rarr;
            </Link>
          </article>

          <article className="valueCard">
            <FontAwesomeIcon icon={faPlug} className="valueIcon" />
            <h3 className="valueTitle">API Integrations</h3>
            <p>
              We connect your apps directly through their APIs &mdash; no duct tape,
              no copy-paste. Your data flows where it needs to, automatically.
            </p>
            <Link className="cta" to="/contact">
              Connect my tools &rarr;
            </Link>
          </article>

          <article className="valueCard">
            <FontAwesomeIcon icon={faBolt} className="valueIcon" />
            <h3 className="valueTitle">Zapier &amp; Automation</h3>
            <p>
              Need it live this week? We build no-code and low-code automations in
              Zapier that quietly handle the busywork around the clock.
            </p>
            <Link className="cta" to="/contact">
              Automate a workflow &rarr;
            </Link>
          </article>

          <article className="valueCard">
            <FontAwesomeIcon icon={faLaptopCode} className="valueIcon" />
            <h3 className="valueTitle">Web Apps</h3>
            <p>
              Custom dashboards, portals, and tools built around how you actually
              work &mdash; fast, secure, and ready to scale with you.
            </p>
            <Link className="cta" to="/contact">
              Build my app &rarr;
            </Link>
          </article>

          <article className="valueCard">
            <FontAwesomeIcon icon={faEnvelopeOpenText} className="valueIcon" />
            <h3 className="valueTitle">Mail Solutions</h3>
            <p>
              Transactional email, automated campaigns, and rock-solid
              deliverability so your messages land in the inbox &mdash; not spam.
            </p>
            <Link className="cta" to="/contact">
              Fix my email &rarr;
            </Link>
          </article>

          <article className="valueCard">
            <FontAwesomeIcon icon={faChartLine} className="valueIcon" />
            <h3 className="valueTitle">Websites &amp; Growth</h3>
            <p>
              Sharp, fast sites and stores tuned for SEO, analytics, and
              conversion &mdash; so the traffic you earn turns into revenue.
            </p>
            <Link className="cta" to="/contact">
              Grow my site &rarr;
            </Link>
          </article>

          <article className="valueCard">
            <FontAwesomeIcon icon={faHeadset} className="valueIcon" />
            <h3 className="valueTitle">AI Chatbots &amp; Agents</h3>
            <p>
              Always-on assistants that answer questions, qualify leads, and book
              meetings &mdash; trained on your content and handing off to your team
              when it counts.
            </p>
            <Link className="cta" to="/contact">
              Add a chatbot &rarr;
            </Link>
          </article>

          <article className="valueCard">
            <FontAwesomeIcon icon={faChartPie} className="valueIcon" />
            <h3 className="valueTitle">Data &amp; Dashboards</h3>
            <p>
              Live dashboards that pull from all your tools into one clear view, so
              you can see what&rsquo;s working and make calls on real numbers &mdash;
              not guesswork.
            </p>
            <Link className="cta" to="/contact">
              See my data &rarr;
            </Link>
          </article>
        </div>
      </section>

      <section className="aboutBlock" aria-label="How we work">
        <div className="aboutBlockHead">
          <span className="aboutEyebrow">How we work</span>
          <h2 className="aboutBlockTitle">A simple path from idea to launch.</h2>
          <p className="aboutBlockLede">
            No bloated process, no jargon &mdash; just a clear route from
            &ldquo;what if&rdquo; to live and working.
          </p>
        </div>

        <ol className="aboutProcess">
          <li className="processStep">
            <span className="stepNum">01</span>
            <FontAwesomeIcon icon={faComments} className="stepIcon" />
            <h3 className="stepTitle">Discover</h3>
            <p>We learn your goals, your stack, and the bottleneck worth solving first.</p>
          </li>
          <li className="processStep">
            <span className="stepNum">02</span>
            <FontAwesomeIcon icon={faPenRuler} className="stepIcon" />
            <h3 className="stepTitle">Design</h3>
            <p>We map the flow and shape a solution that&rsquo;s simple to use and easy to grow.</p>
          </li>
          <li className="processStep">
            <span className="stepNum">03</span>
            <FontAwesomeIcon icon={faCode} className="stepIcon" />
            <h3 className="stepTitle">Build</h3>
            <p>We develop, connect, and test it against the real world &mdash; not just a demo.</p>
          </li>
          <li className="processStep">
            <span className="stepNum">04</span>
            <FontAwesomeIcon icon={faRocket} className="stepIcon" />
            <h3 className="stepTitle">Launch</h3>
            <p>We ship, monitor, and keep improving so it pays off long after go-live.</p>
          </li>
        </ol>
      </section>

      <section className="aboutCta" aria-label="Start a project">
        <div className="aboutCtaInner">
          <h2 className="valueTitle aboutCtaTitle">
            Let&rsquo;s build something that works.
          </h2>
          <p>
            Tell us what you&rsquo;re trying to do and we&rsquo;ll get back to you
            within one business day &mdash; no pressure, just a real conversation
            about what you need.
          </p>
          <div className="aboutCtaActions">
            <Link className="btn-primary" to="/contact">
              Start a project
            </Link>
            <Link className="cta aboutCtaLink" to="/work">
              See the work &rarr;
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default About;
